import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as Yup from "yup"
import { useFormik } from 'formik'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import { httpsCallable } from "firebase/functions";
import Grid from '@mui/material/Unstable_Grid2/Grid2'
import { useDispatch, useSelector } from 'react-redux'
import { functions } from "../../../Infrastructure/config";
import { StepsHeader } from '../../Common/StepsHeader/stepsHeader'
import {
  StyledOutlinedTextField
} from '../../../Infrastructure/Theme/styledComponents';
import { ErrorBlockSmall, learningObjectivesStyles, QuestionName } from '../styles'
import { decrementCoursesSteps, incrementCoursesSteps } from '../../../Infrastructure/States/coursesStepsSlice'
import { StepsFooter } from '../../Common/StepsFooter/stepsFooter';
import { useLocation, useNavigate } from 'react-router-dom';
import { Loader } from '../../Common/Loader/loader';
import { cleanCourseState, setCourseDetails } from '../../../Infrastructure/States/courseDetailsSlice';
import { ShowErrorToast } from '../../Common/Toast/toast';
import ERROR_CONSTANTS from '../../../Infrastructure/Constants/errorConstants';
import paths from '../../../Infrastructure/Router/paths';

export const LearningObjectives = () => {
    const scrollRef = useRef(null);
    const objective1Ref = useRef(null);
    const objective2Ref = useRef(null);
    const objective3Ref = useRef(null);
    const objective4Ref = useRef(null);
    const objective5Ref = useRef(null);

    const prerequisite1Ref = useRef(null);
    const prerequisite2Ref = useRef(null);
    const prerequisite3Ref = useRef(null);
    const prerequisite4Ref = useRef(null);
    const prerequisite5Ref = useRef(null);

    const intendedLearnerRef = useRef(null);

    const refs = useMemo(
        () => ({
            objective1: objective1Ref,
            objective2: objective2Ref,
            objective3: objective3Ref,
            objective4: objective4Ref,
            objective5: objective5Ref,
            prerequisite1: prerequisite1Ref,
            prerequisite2: prerequisite2Ref,
            prerequisite3: prerequisite3Ref,
            prerequisite4: prerequisite4Ref,
            prerequisite5: prerequisite5Ref,
            intendedLearner: intendedLearnerRef,
        }),
        []
    );
    const theme = useTheme()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const formikRef = useRef(null)
    const { pathname } = useLocation()
    const [error, setError] = useState('')
    const isXs = useMediaQuery(theme.breakpoints.down('sm'));
    const [isLoading, setIsLoading] = useState(false);
    const ip = useSelector((state) => state.ip.address)
    const page = useSelector((state)=> state.route.currentRoute)
    const course = useSelector((state) => state.course.courseDetails)
    const userData = useSelector((state) => state.userData.data)
    const courseId = course?.courseId || null

    const prerequisites = course?.course?.courseDetails?.intended_learner?.prerequisites || {}
    const objectives = course?.course?.courseDetails?.intended_learner?.objectives || {}
    const intendedLearner = course?.course?.courseDetails?.intended_learner?.description

    const objective1 = objectives?.objective_1
    const objective2 = objectives?.objective_2
    const objective3 = objectives?.objective_3
    const objective4 = objectives?.objective_4
    const objective5 = objectives?.objective_5
    const prerequisite1 = prerequisites?.prerequisite_1
    const prerequisite2 = prerequisites?.prerequisite_2
    const prerequisite3 = prerequisites?.prerequisite_3
    const prerequisite4 = prerequisites?.prerequisite_4
    const prerequisite5 = prerequisites?.prerequisite_5

    const courseSteps = useSelector((state) => state.courseSteps.courseSteps)
    const handleExit = async () => {
        try {
            setIsLoading(true)
            const updateCourseObjectives = httpsCallable(functions, "updatecourseobjectives")
            await updateCourseObjectives({ ...formik.values, step: 2, ip, page })
            dispatch(cleanCourseState())
            formik.resetForm()
            navigate(paths.dashboardPage.path)
        } catch (err) {
            navigate(paths.dashboardPage.path)
            ShowErrorToast(ERROR_CONSTANTS.GENERIC_ERROR_TOAST)
        } finally {
            setIsLoading(false)
        }
    }
    const handleDec = async () => {
        if (courseSteps > 1) {
            try {
                setIsLoading(true)
                const updateCourseObjectives = httpsCallable(functions, "updatecourseobjectives")
                await updateCourseObjectives({ ...formik.values, step: 1, ip, page })
                if (courseId && userData && userData.educator && userData.educator.courses) {
                    const course = userData?.educator?.courses[courseId ? courseId : ''];
                    if (course) {
                        dispatch(setCourseDetails({ course, courseId }));
                    }
                }
                dispatch(decrementCoursesSteps())
            } catch (err) {
                ShowErrorToast(ERROR_CONSTANTS.GENERIC_ERROR_TOAST)
            setError(ERROR_CONSTANTS.GENERIC_ERROR_TEXT)
            } finally {
                setIsLoading(false)
            }
        }
    }
    const handleInc = async () => {
        if (courseSteps > 1) {
            dispatch(incrementCoursesSteps())
            const course = userData?.educator?.courses[courseId ? courseId : ''];
            if (course) {
                dispatch(setCourseDetails({ course, courseId }));
            }
        }
    }
    const formik = useFormik({
        initialValues: {
            objective1: objective1 || "",
            objective2: objective2 || "",
            objective3: objective3 || "",
            objective4: objective4 || "",
            objective5: objective5 || "",
            prerequisite1: prerequisite1 || "",
            prerequisite2: prerequisite2 || "",
            prerequisite3: prerequisite3 || "",
            prerequisite4: prerequisite4 || "",
            prerequisite5: prerequisite5 || "",
            intendedLearner: intendedLearner || "",
            status: "in_progress",
            courseId: courseId
        },
        validationSchema: Yup.object().shape({
            objective1: Yup.string().required("Objective 1 is Required"),
            objective2: Yup.string().required("Objective 2 is Required"),
            prerequisite1: Yup.string().required("Prerequisite 1 is Required"),
            intendedLearner: Yup.string().required("Who this course is for Required"),
        }),
        onSubmit: async () => {
            const formValues = { ...formik.values, step: 3, ip, page }
            if (courseSteps >= 1 && courseSteps <= 6) {
                try {
                    setIsLoading(true)
                    const updateCourseObjectives = httpsCallable(functions, "updatecourseobjectives")
                    await updateCourseObjectives(formValues)
                    handleInc();
                } catch (error) {
                    ShowErrorToast(ERROR_CONSTANTS.GENERIC_ERROR_TOAST)
            setError(ERROR_CONSTANTS.GENERIC_ERROR_TEXT)
                } finally {
                    setIsLoading(false)
                }
            }
        },
    })
    formikRef.current = formik;
    const handleContinueClick = () => {
        if (formikRef.current) {
            formikRef?.current.handleSubmit();
        }
    };
    useEffect(() => {
        if (!isXs) return
        if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
            const firstErrorKey = Object.keys(formik.errors)[0];
            const ref = refs[firstErrorKey]?.current;

            if (ref && scrollRef.current) {
                const container = scrollRef.current;
                const elementTop = ref.getBoundingClientRect().top;
                const containerTop = container.getBoundingClientRect().top;

                container.scrollTo({
                    top: container.scrollTop + (elementTop - containerTop) - 100,
                    behavior: "smooth"
                });
            }
        }
    }, [formik.errors, formik.submitCount, refs, isXs])
    
    useEffect(() => {
        document.documentElement.scrollTop = 0
        document.body.scrollTop = 0
    }, [pathname])
    return (
        <Box sx={learningObjectivesStyles.container} >
             <Box sx={learningObjectivesStyles.headerBox}>
                <StepsHeader steps={courseSteps} handleExit={handleExit} />
            </Box>
            <Box component={"form"} onSubmit={formik.handleSubmit}  
                sx={learningObjectivesStyles.formContainer}
                ref={scrollRef}
            >
                {
                    isLoading ? <Loader/> :
                        <Grid container spacing={3} >
                            <Grid xs={12} md={6} lg={4} xl={4} spacing={2}>
                                <Box sx={learningObjectivesStyles.questionBox}>
                                    <QuestionName variant="h6">
                                        What are some learning objectives that learners can expect to achieve after completing your course?
                                    </QuestionName>
                                </Box>
                                <Box sx={learningObjectivesStyles.gridBox}>
                                    <StyledOutlinedTextField
                                        name="objective1"
                                        label={
                                            formik.touched.objective1 && Boolean(formik.errors.objective1)
                                                ? formik.errors.objective1
                                                : "Objective 1"
                                        }
                                        error={formik.touched.objective1 && Boolean(formik.errors.objective1)}
                                        variant="outlined"
                                        inputRef={refs.objective1}
                                        onChange={formik.handleChange}
                                        value={formik.values.objective1}
                                        fullWidth
                                    />
                                    <StyledOutlinedTextField
                                        name="objective2"
                                        label={
                                            formik.touched.objective2 && Boolean(formik.errors.objective2)
                                                ? formik.errors.objective2
                                                : "Objective 2"
                                        }
                                        error={formik.touched.objective2 && Boolean(formik.errors.objective2)}
                                        variant="outlined"
                                        inputRef={refs.objective2}
                                        onChange={formik.handleChange}
                                        value={formik.values.objective2}
                                        fullWidth
                                    />
                                    <StyledOutlinedTextField
                                        name="objective3"
                                        label={
                                            formik.touched.objective3 && Boolean(formik.errors.objective3)
                                                ? formik.errors.objective3
                                                : "Objective 3"
                                        }
                                        error={formik.touched.objective3 && Boolean(formik.errors.objective3)}
                                        variant="outlined"
                                        inputRef={refs.objective3}
                                        onChange={formik.handleChange}
                                        value={formik.values.objective3}
                                        fullWidth
                                    />
                                    <StyledOutlinedTextField
                                        name="objective4"
                                        label={
                                            formik.touched.objective4 && Boolean(formik.errors.objective4)
                                                ? formik.errors.objective4
                                                : "Objective 4"
                                        }
                                        error={formik.touched.objective4 && Boolean(formik.errors.objective4)}
                                        variant="outlined"
                                        inputRef={refs.objective4}
                                        onChange={formik.handleChange}
                                        value={formik.values.objective4}
                                        fullWidth
                                    />
                                    <StyledOutlinedTextField
                                        name="objective5"
                                        label={
                                            formik.touched.objective5 && Boolean(formik.errors.objective5)
                                                ? formik.errors.objective5
                                                : "Objective 5"
                                        }
                                        error={formik.touched.objective5 && Boolean(formik.errors.objective5)}
                                        variant="outlined"
                                        inputRef={refs.objective5}
                                        onChange={formik.handleChange}
                                        value={formik.values.objective5}
                                        fullWidth
                                    />
                                </Box>

                            </Grid>
                            <Grid xs={12} md={6} lg={4} xl={4}>
                                <Box sx={learningObjectivesStyles.questionBox}>
                                    <QuestionName variant="h6">
                                        Are there any prerequisites or requirements for taking your course?
                                    </QuestionName>
                                </Box>
                                <Box sx={learningObjectivesStyles.gridBox}>
                                    <StyledOutlinedTextField
                                        name="prerequisite1"
                                        label={
                                            formik.touched.prerequisite1 && Boolean(formik.errors.prerequisite1)
                                                ? formik.errors.prerequisite1
                                                : "Prerequisite 1"
                                        }
                                        error={formik.touched.prerequisite1 && Boolean(formik.errors.prerequisite1)}
                                        variant="outlined"
                                        inputRef={refs.prerequisite1}
                                        onChange={formik.handleChange}
                                        value={formik.values.prerequisite1}
                                        fullWidth
                                    />
                                    <StyledOutlinedTextField
                                        name="prerequisite2"
                                        label={
                                            formik.touched.prerequisite2 && Boolean(formik.errors.prerequisite2)
                                                ? formik.errors.prerequisite2
                                                : "Prerequisite 2"
                                        }
                                        error={formik.touched.prerequisite2 && Boolean(formik.errors.prerequisite2)}
                                        variant="outlined"
                                        inputRef={refs.prerequisite2}
                                        onChange={formik.handleChange}
                                        value={formik.values.prerequisite2}
                                        fullWidth
                                    />
                                    <StyledOutlinedTextField
                                        name="prerequisite3"
                                        label={
                                            formik.touched.prerequisite3 && Boolean(formik.errors.prerequisite3)
                                                ? formik.errors.prerequisite3
                                                : "Prerequisite 3"
                                        }
                                        error={formik.touched.prerequisite3 && Boolean(formik.errors.prerequisite3)}
                                        variant="outlined"
                                        inputRef={refs.prerequisite3}
                                        onChange={formik.handleChange}
                                        value={formik.values.prerequisite3}
                                        fullWidth
                                    />
                                    <StyledOutlinedTextField
                                        name="prerequisite4"
                                        label={
                                            formik.touched.prerequisite4 && Boolean(formik.errors.prerequisite4)
                                                ? formik.errors.prerequisite4
                                                : "Prerequisite 4"
                                        }
                                        error={formik.touched.prerequisite4 && Boolean(formik.errors.prerequisite4)}
                                        variant="outlined"
                                        inputRef={refs.prerequisite4}
                                        onChange={formik.handleChange}
                                        value={formik.values.prerequisite4}
                                        fullWidth
                                    />
                                    <StyledOutlinedTextField
                                        name="prerequisite5"
                                        label={
                                            formik.touched.prerequisite5 && Boolean(formik.errors.prerequisite5)
                                                ? formik.errors.prerequisite5
                                                : "Prerequisite 5"
                                        }
                                        error={formik.touched.prerequisite5 && Boolean(formik.errors.prerequisite5)}
                                        variant="outlined"
                                        inputRef={refs.prerequisite5}
                                        onChange={formik.handleChange}
                                        value={formik.values.prerequisite5}
                                        fullWidth
                                    />
                                </Box>
                            </Grid>
                            <Grid xs={12} md={12} lg={4} xl={4} spacing={2}>
                                <Box sx={learningObjectivesStyles.questionBox}>
                                    <QuestionName variant="h6">
                                        Who this course is for
                                    </QuestionName>
                                </Box>
                                <Box>
                                    <StyledOutlinedTextField
                                        name="intendedLearner"
                                        label={
                                            formik.touched.intendedLearner && Boolean(formik.errors.intendedLearner)
                                                ? formik.errors.intendedLearner
                                                : "Who this course is for"
                                        }
                                        error={formik.touched.intendedLearner && Boolean(formik.errors.intendedLearner)}
                                        variant="outlined"
                                        inputRef={refs.intendedLearner}
                                        placeholder='Describe who could benefit from taking this course'
                                        onChange={formik.handleChange}
                                        value={formik.values.intendedLearner}
                                        multiline
                                        rows={12}
                                        fullWidth
                                    />
                                </Box>

                            </Grid>
                        </Grid>
                }
                <ErrorBlockSmall>
                    {error}
                </ErrorBlockSmall>
            </Box>
            <Box sx={learningObjectivesStyles.footerBox}>
                <StepsFooter handleDec={handleDec} handleContinueClick={handleContinueClick} error={error} isLoading={isLoading} />
            </Box>
        </Box>
    )
}